/*
 * Big Popa Pizza — custom pizza builder.
 *
 * A plain state machine. No framework and no build step, so it drops straight
 * into the theme. Price and calories recompute locally on every change; nothing
 * is fetched while the customer is building.
 *
 * Pricing is deliberately pluggable. The UI never knows how an upcharge reaches
 * the cart — that is decided by rules.pricingMode, so switching between an
 * options app, a hidden upcharge line item, or a future Cart Transform function
 * does not touch anything below the addToCart() boundary.
 */

class BppPizzaBuilder extends HTMLElement {
  static GROUP_TO_COLLECTION = {
    size: 'sizes',
    crust: 'crusts',
    sauce: 'sauces',
    cheese: 'cheeses',
    style: 'styles',
  };

  connectedCallback() {
    const configNode = this.querySelector('[data-bpp-builder-config]');
    if (!configNode) return;

    try {
      this.config = JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('Pizza builder config is not valid JSON', error);
      return;
    }

    this.rules = this.config.rules || {};
    this.strings = this.config.strings || {};
    this.productTitle = this.dataset.productTitle || 'Custom pizza';
    this.step = 1;
    this.activeHalf = 'left';

    this.cacheNodes();
    this.state = this.stateFromUrl() || this.initialState();
    this.bindEvents();
    this.renderAll();
    this.reportUnmatchedSizes();
    this.reserveSpaceForBar();
    this.compactPreview();
  }

  /**
   * Phone and tablet: the pizza is large at the top of the page and shrinks to
   * a pinned strip once the heading has scrolled away. The strip size matches
   * the old fixed one (30vw, 112-200px), which the panels' scroll-margin uses.
   */
  compactPreview() {
    const preview = this.querySelector('[data-bpp-preview]');
    const stage = preview?.querySelector('.bpp-bld__stage');
    const head = this.querySelector('.bpp-bld__head');
    if (!preview || !stage || !head) return;
    const mq = window.matchMedia('(max-width: 989px)');

    let frame = 0;
    const update = () => {
      frame = 0;
      if (!mq.matches) {
        preview.classList.remove('is-compact');
        return;
      }
      const big = stage.offsetWidth;
      const small = Math.min(200, Math.max(112, window.innerWidth * 0.3));
      const shift = Math.max(0, big - small);
      preview.style.setProperty('--bpp-compact-scale', big ? String(Math.min(1, small / big)) : '1');
      preview.style.setProperty('--bpp-compact-shift', `${shift}px`);
      preview.style.setProperty('--bpp-compact-h', `${Math.max(0, preview.offsetHeight - shift)}px`);
      const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bpp-header-h')) || 0;
      preview.classList.toggle('is-compact', shift > 0 && head.getBoundingClientRect().bottom < headerH);
    };
    this.onPreviewScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', this.onPreviewScroll, { passive: true });
    window.addEventListener('resize', this.onPreviewScroll);
    update();
  }

  /**
   * The total bar is always on screen, so the page has to end above it.
   * Measured rather than hardcoded: the bar grows when the price or the button
   * label wraps, which happens on a landscape phone and in French.
   */
  reserveSpaceForBar() {
    const bar = this.querySelector('.bpp-bld__bar');
    if (!bar) return;

    const apply = () => {
      const h = bar.offsetHeight;
      document.documentElement.style.setProperty('--bpp-sticky-bar-h', `${h}px`);
      document.body.classList.add('bpp-has-sticky-bar');
    };

    if ('ResizeObserver' in window) {
      this.barObserver = new ResizeObserver(apply);
      this.barObserver.observe(bar);
    } else {
      window.addEventListener('resize', apply);
    }
    apply();
  }

  disconnectedCallback() {
    this.barObserver?.disconnect();
    if (this.onPreviewScroll) {
      window.removeEventListener('scroll', this.onPreviewScroll);
      window.removeEventListener('resize', this.onPreviewScroll);
    }
    document.body.classList.remove('bpp-has-sticky-bar');
    document.documentElement.style.removeProperty('--bpp-sticky-bar-h');
  }

  /* ------------------------------------------------------------ i18n */

  /**
   * Look up a translated string and fill its [bracket] placeholders.
   * Bracket syntax is used so Liquid's `t` filter leaves the tokens intact —
   * the values are only known once the customer starts interacting.
   */
  s(key, vars) {
    let out = this.strings[key];
    if (out == null) return '';
    if (vars) {
      Object.keys(vars).forEach((name) => {
        out = out.split(`[${name}]`).join(String(vars[name]));
      });
    }
    return out;
  }

  /* ---------------------------------------------------------------- setup */

  cacheNodes() {
    this.els = {
      summary: this.querySelector('[data-bpp-summary]'),
      total: this.querySelector('[data-bpp-total]'),
      calories: this.querySelector('[data-bpp-calories]'),
      serves: this.querySelector('[data-bpp-serves]'),
      addBtn: this.querySelector('[data-bpp-add]'),
      addLabel: this.querySelector('[data-bpp-add-label]'),
      error: this.querySelector('[data-bpp-error]'),
      prev: this.querySelector('[data-bpp-prev]'),
      next: this.querySelector('[data-bpp-next]'),
      halves: this.querySelector('[data-bpp-halves]'),
      halfToggle: this.querySelector('[data-bpp-half-toggle]'),
      layers: this.querySelector('[data-bpp-layers]'),
      halfLine: this.querySelector('[data-bpp-half-line]'),
      previewNote: this.querySelector('[data-bpp-preview-note]'),
      toppingGroups: this.querySelector('[data-bpp-topping-groups]'),
      toppingHint: this.querySelector('[data-bpp-topping-hint]'),
      review: this.querySelector('[data-bpp-review]'),
      reset: this.querySelector('[data-bpp-reset]'),
      panels: [...this.querySelectorAll('[data-bpp-panel]')],
      stepBtns: [...this.querySelectorAll('[data-bpp-step-btn]')],
    };
  }

  /**
   * The option the builder opens on: the one marked "Start selected" (for
   * sizes, the "Default size"), else the first available one, so the builder
   * always starts valid. The shipped template starts on a medium Napolitana.
   */
  firstOf(group) {
    const list = this.config[group] || [];
    return (
      list.find((o) => o.default && o.available !== false) ||
      list.find((o) => o.available !== false) ||
      list[0] ||
      null
    );
  }

  initialState() {
    return {
      size: this.firstOf('sizes')?.id || null,
      crust: this.firstOf('crusts')?.id || null,
      sauce: this.firstOf('sauces')?.id || null,
      cheese: this.firstOf('cheeses')?.id || null,
      style: this.firstOf('styles')?.id || null,
      halfAndHalf: false,
      toppings: { left: [], right: [], whole: [] },
    };
  }

  /* ---------------------------------------------------------- shareable URL
     A half-and-half with eight toppings is real work to assemble, so the state
     has to survive a reload and be sendable to whoever is paying. Encoded as
     short readable params rather than a blob:

       ?size=medium&crust=classic&sauce=red&cheese=light&style=custom
       &half=1&left=pepperoni,bacon&right=mushrooms&whole=olives

     Only values that differ from the default are written, so an untouched
     builder keeps a clean URL. Every id is validated against the config on the
     way back in — a stale or hand-edited link falls back to the default for
     that group instead of putting an unbuyable option in the cart.

     THEME NOTE: this is section JS and carries over unchanged. If the store
     later gets a "save my pizza" account feature, this is the serialisation to
     reuse. */
  stateFromUrl() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return null; }
    const groups = ['size', 'crust', 'sauce', 'cheese', 'style'];
    if (!groups.some((g) => params.has(g)) && !params.has('half')
        && !['left', 'right', 'whole'].some((k) => params.has(k))) return null;

    const state = this.initialState();

    groups.forEach((group) => {
      const raw = params.get(group);
      if (!raw) return;
      const collection = BppPizzaBuilder.GROUP_TO_COLLECTION[group];
      const option = collection ? this.find(collection, raw) : null;
      if (option && option.available !== false) state[group] = raw;
    });

    const valid = (list) => {
      const seen = {};
      return (list || '').split(',').filter(Boolean).filter((id) => {
        const t = this.find('toppings', id);
        if (!t || t.available === false) return false;
        seen[id] = (seen[id] || 0) + 1;
        return seen[id] <= BppPizzaBuilder.MAX_PORTIONS;
      });
    };

    state.halfAndHalf = params.get('half') === '1';
    if (state.halfAndHalf) {
      state.toppings.left = valid(params.get('left'));
      state.toppings.right = valid(params.get('right'));
      state.toppings.whole = [];
    } else {
      state.toppings.whole = valid(params.get('whole') || params.get('left'));
      state.toppings.left = [];
      state.toppings.right = [];
    }
    return state;
  }

  syncUrl() {
    if (!window.history || !window.history.replaceState) return;
    const base = this.initialState();
    const params = new URLSearchParams();
    ['size', 'crust', 'sauce', 'cheese', 'style'].forEach((group) => {
      if (this.state[group] && this.state[group] !== base[group]) params.set(group, this.state[group]);
    });
    if (this.state.halfAndHalf) {
      params.set('half', '1');
      if (this.state.toppings.left.length) params.set('left', this.state.toppings.left.join(','));
      if (this.state.toppings.right.length) params.set('right', this.state.toppings.right.join(','));
    } else if (this.state.toppings.whole.length) {
      params.set('whole', this.state.toppings.whole.join(','));
    }
    const qs = params.toString();
    /* replaceState so Back leaves the builder rather than stepping through
       every topping the customer tried. */
    window.history.replaceState({}, '', qs
      ? window.location.pathname + '?' + qs
      : window.location.pathname);
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const option = event.target.closest('[data-bpp-choose]');
      if (option) {
        this.choose(option.dataset.bppChoose, option.dataset.bppValue);
        return;
      }

      const double = event.target.closest('[data-bpp-double]');
      if (double) {
        this.toggleDouble(double.dataset.bppDouble);
        return;
      }

      const topping = event.target.closest('[data-bpp-topping]');
      if (topping) {
        this.toggleTopping(topping.dataset.bppTopping);
        return;
      }

      const half = event.target.closest('[data-bpp-half]');
      if (half) {
        this.activeHalf = half.dataset.bppHalf;
        this.renderHalves();
        this.renderToppings();
        return;
      }

      const stepBtn = event.target.closest('[data-bpp-step-btn]');
      if (stepBtn) {
        this.goToStep(Number(stepBtn.dataset.bppStepBtn));
      }
    });

    this.els.halfToggle?.addEventListener('click', () => this.toggleHalfAndHalf());
    this.els.prev?.addEventListener('click', () => this.goToStep(this.step - 1));
    this.els.next?.addEventListener('click', () => this.goToStep(this.step + 1));
    this.els.reset?.addEventListener('click', () => this.reset());
    this.els.addBtn?.addEventListener('click', () => this.addToCart());
  }

  /**
   * Theme-editor only. A size with no matching variant still renders, priced
   * from its manual field, but cannot be added to the cart — so without this
   * the builder looks correct in the editor and fails for shoppers. Driven by
   * the fromVariant flag the data snippet already emits, so the match logic
   * lives in exactly one place.
   */
  reportUnmatchedSizes() {
    if (this.dataset.designMode !== 'true') return;
    const target = this.querySelector('[data-bpp-unmatched]');
    if (!target) return;

    const notes = [];

    const unmatched = (this.config.sizes || []).filter((s) => !s.fromVariant);
    if (unmatched.length) {
      const names = unmatched.map((s) => s.label).filter(Boolean).join(', ');
      notes.push(
        `Not linked to a variant: ${names}. These render with their manual ` +
          "price but cannot be added to the cart. Set each one's Variant ID."
      );
    }

    if (this.rules.pricingMode === 'none') {
      notes.push(
        'Option pricing is set to "an options app handles upcharges", so crust, ' +
          'cheese, style and topping prices are shown on the tiles but are not ' +
          'added to the total or the cart by this section.'
      );
    } else if (this.rules.pricingMode === 'extras_product' && !(this.rules.extrasVariants || []).length) {
      notes.push(
        'Option pricing is set to the "Pizza extras" line, but no Pizza extras ' +
          'product with priced variants is linked, so a pizza with paid extras ' +
          'cannot be added to the cart.'
      );
    } else if (this.rules.pricingMode === 'upcharge_product' && !this.rules.upchargeVariantId) {
      notes.push(
        'Option pricing is set to the hidden upcharge line item, but no ' +
          'Upcharge variant ID is set — upcharges will not reach the cart.'
      );
    }

    if (!notes.length) {
      target.hidden = true;
      return;
    }
    target.textContent = notes.join(' ');
    target.hidden = false;
  }

  /* --------------------------------------------------------------- state */

  choose(group, id) {
    if (this.busy) return;
    if (this.state[group] === id) return;

    // The markup disables unavailable options, but the state must not depend on
    // the view enforcing that — a sold-out size selected here would reach the
    // cart with a variant that cannot be bought.
    const collection = BppPizzaBuilder.GROUP_TO_COLLECTION[group];
    const option = collection ? this.find(collection, id) : null;
    if (!option || option.available === false) return;

    this.state[group] = id;
    this.renderAll();
  }

  toggleHalfAndHalf() {
    if (this.busy) return;
    this.state.halfAndHalf = !this.state.halfAndHalf;

    if (this.state.halfAndHalf) {
      // Splitting: everything currently on the whole pizza applies to both
      // halves, which is what the customer sees on screen right now.
      const whole = this.state.toppings.whole;
      this.state.toppings.left = BppPizzaBuilder.mergePortions(this.state.toppings.left, whole);
      this.state.toppings.right = BppPizzaBuilder.mergePortions(this.state.toppings.right, whole);
      this.state.toppings.whole = [];
      this.activeHalf = 'left';
    } else {
      // Merging back: a topping on either half becomes a whole-pizza topping,
      // so nothing the customer picked silently disappears.
      const merged = BppPizzaBuilder.mergePortions(this.state.toppings.left, this.state.toppings.right);
      let whole = BppPizzaBuilder.mergePortions(this.state.toppings.whole, merged);

      // The cap is per half, so two full halves can hold twice the cap between
      // them. Folding them into one pizza has to re-apply it, or a 7-topping
      // limit quietly becomes 14 and the hint reads "14 of 7 chosen".
      const max = Number(this.rules.maxToppings) || 0;
      if (max > 0 && whole.length > max) {
        whole = whole.slice(0, max);
        this.showError(this.s('maxToppings', { max }));
      }

      this.state.toppings.whole = whole;
      this.state.toppings.left = [];
      this.state.toppings.right = [];
    }

    this.renderAll();
  }

  toggleTopping(id) {
    if (this.busy) return;
    if (!this.findTopping(id)) return;
    const key = this.state.halfAndHalf ? this.activeHalf : 'whole';
    const list = this.state.toppings[key];
    const portions = BppPizzaBuilder.portionsOf(list, id);

    // The tile only adds or removes, so one tap always takes a topping off,
    // single or double. Doubling has its own button (toggleDouble).
    if (portions > 0) {
      this.state.toppings[key] = list.filter((x) => x !== id);
    } else {
      const max = Number(this.rules.maxToppings) || 0;
      if (max > 0 && this.toppingCount() >= max) {
        this.showError(
          this.s(this.state.halfAndHalf ? 'maxToppingsPerHalf' : 'maxToppings', { max })
        );
        return;
      }
      list.push(id);
    }

    this.clearError();
    this.renderAll();
  }

  /** The "+ Double" button under a chosen topping: one portion <-> two. */
  toggleDouble(id) {
    if (this.busy) return;
    if (!this.findTopping(id)) return;
    const key = this.state.halfAndHalf ? this.activeHalf : 'whole';
    const list = this.state.toppings[key];
    const portions = BppPizzaBuilder.portionsOf(list, id);
    if (portions === 0) return;

    if (portions >= BppPizzaBuilder.MAX_PORTIONS) {
      list.splice(list.lastIndexOf(id), 1);
    } else {
      const max = Number(this.rules.maxToppings) || 0;
      if (max > 0 && this.toppingCount() >= max) {
        this.showError(
          this.s(this.state.halfAndHalf ? 'maxToppingsPerHalf' : 'maxToppings', { max })
        );
        return;
      }
      list.push(id);
    }

    this.clearError();
    this.renderAll();
  }

  reset() {
    this.state = this.initialState();
    this.activeHalf = 'left';
    this.clearError();
    this.goToStep(1);
    this.renderAll();
  }

  goToStep(step) {
    const max = this.els.panels.length;
    // Next hides itself on the last step and Back hides itself on the first, so
    // a keyboard user who presses either can be left with focus on a hidden
    // element -- the browser drops them to the top of the document. Remember
    // what was pressed, and if it has gone, land the user on the new panel.
    const trigger = this.ownerDocument.activeElement;

    this.step = Math.min(Math.max(step, 1), max);
    this.renderSteps();

    const panel = this.querySelector(`[data-bpp-panel="${this.step}"]`);
    const triggerGone =
      trigger && (trigger.hidden || trigger.closest?.('[hidden]') || !trigger.isConnected);
    if (triggerGone) panel?.focus({ preventScroll: true });

    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    panel?.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  }

  /* --------------------------------------------------------------- lookup */

  find(group, id) {
    return (this.config[group] || []).find((o) => o.id === id) || null;
  }

  findTopping(id) {
    return (this.config.toppings || []).find((t) => t.id === id) || null;
  }

  selected() {
    return {
      size: this.find('sizes', this.state.size),
      crust: this.find('crusts', this.state.crust),
      sauce: this.find('sauces', this.state.sauce),
      cheese: this.find('cheeses', this.state.cheese),
      style: this.find('styles', this.state.style),
    };
  }

  /** Every chosen topping, flattened, with where it sits. */
  toppingEntries() {
    const entries = [];
    ['whole', 'left', 'right'].forEach((where) => {
      this.state.toppings[where].forEach((id) => {
        const topping = this.findTopping(id);
        if (topping) entries.push({ topping, where });
      });
    });
    return entries;
  }

  /**
   * Billable topping count, used for pricing only. A topping on one half counts
   * as a full topping unless the merchant has chosen half-price halves.
   */
  toppingUnits() {
    const halfWeight = this.rules.halfCharge === 'half' ? 0.5 : 1;
    return this.toppingEntries().reduce(
      (sum, { where }) => sum + (where === 'whole' ? 1 : halfWeight),
      0
    );
  }

  /**
   * How many toppings the cap has to judge. Deliberately not toppingUnits():
   * the cap is about how much fits on a pizza, and "up to 7 toppings" means
   * seven per half, not seven shared between two halves. It also must not move
   * when the merchant switches half-price billing on.
   */
  toppingCount() {
    const key = this.state.halfAndHalf ? this.activeHalf : 'whole';
    return this.state.toppings[key].length;
  }

  /* --------------------------------------------------------------- pricing */

  /** Everything the linked variant already covers. */
  basePriceCents() {
    return this.selected().size?.price || 0;
  }

  /** Everything the variant does not cover — the part needing a pricing mode. */
  /**
   * What the upcharge line item will actually cost, after the cart quantises it
   * into whole units of upchargeUnitCents. Quoting the exact figure while
   * charging the rounded one means the customer is billed a few cents either
   * side of the price they agreed to, on every order.
   */
  chargeableUpchargeCents() {
    const exact = this.upchargeCents();
    if (this.rules.pricingMode === 'extras_product') {
      const plan = this.extrasPlan(exact);
      return plan.length ? plan.reduce((sum, line) => sum + line.cents * line.qty, 0) : exact;
    }
    if (this.rules.pricingMode !== 'upcharge_product') return exact;
    const unit = Number(this.rules.upchargeUnitCents) || 1;
    return Math.round(exact / unit) * unit;
  }

  /**
   * How an upcharge is charged in extras_product mode: the "Pizza extras"
   * variants, one per price, cheapest first ([cents, id]). One variant that
   * covers the amount; past the dearest variant, as many of it as fit plus one
   * for the rest. Covering rounds up, so the customer is never charged less
   * than quoted -- and chargeableUpchargeCents() quotes this exact sum.
   * Returns [] when there is nothing to charge or no variants to charge with.
   */
  extrasPlan(cents) {
    const table = this.rules.extrasVariants || [];
    if (cents <= 0 || !table.length) return [];
    const [maxCents, maxId] = table[table.length - 1];
    const plan = [];
    let rest = cents;
    if (rest > maxCents) {
      const whole = Math.floor(rest / maxCents);
      plan.push({ id: maxId, cents: maxCents, qty: whole });
      rest -= whole * maxCents;
    }
    if (rest > 0) {
      const [fitCents, fitId] = table.find(([price]) => price >= rest);
      const same = plan.find((line) => line.id === fitId);
      if (same) same.qty += 1;
      else plan.push({ id: fitId, cents: fitCents, qty: 1 });
    }
    return plan;
  }

  upchargeCents() {
    const { crust, sauce, cheese, style } = this.selected();
    let total = 0;

    total += crust?.price || 0;
    total += sauce?.price || 0;
    total += cheese?.price || 0;
    total += style?.price || 0;

    const included = Number(this.rules.includedToppings) || 0;
    const perExtra = Number(this.rules.pricePerExtra) || 0;
    const halfWeight = this.rules.halfCharge === 'half' ? 0.5 : 1;

    // Premium toppings carry their own price and are always charged.
    // Everything else draws down the included allowance first.
    let allowance = included;
    this.toppingEntries().forEach(({ topping, where }) => {
      const weight = where === 'whole' ? 1 : halfWeight;

      if (topping.price) {
        total += Math.round(topping.price * weight);
        return;
      }

      if (allowance >= weight) {
        allowance -= weight;
        return;
      }

      const chargeable = weight - Math.max(allowance, 0);
      allowance = 0;
      total += Math.round(perExtra * chargeable);
    });

    return Math.round(total);
  }

  /**
   * What the customer will actually be charged.
   *
   * In 'none' mode the theme adds only the variant to the cart — an options app
   * owns the upcharges — so folding them into this figure would quote a total
   * the cart never charges. Option tiles still show their "+ $x" so the
   * customer can see what each choice costs.
   */
  totalCents() {
    const chargedHere = this.rules.pricingMode === 'none' ? 0 : this.chargeableUpchargeCents();
    return this.basePriceCents() + chargedHere;
  }

  calorieRange() {
    const { size, crust, sauce, cheese, style } = this.selected();
    let total = (size?.baseCalories || 0) + (crust?.calories || 0) +
      (sauce?.calories || 0) + (cheese?.calories || 0);

    const halfWeight = 0.5;
    this.toppingEntries().forEach(({ topping, where }) => {
      total += (topping.calories || 0) * (where === 'whole' ? 1 : halfWeight);
    });

    total *= style?.calorieMultiplier || 1;
    if (total <= 0) return null;

    // Presented as a band, because portioning varies pizza to pizza.
    return { low: Math.round((total * 0.92) / 10) * 10, high: Math.round((total * 1.08) / 10) * 10 };
  }

  money(cents) {
    return BppPizzaBuilder.formatMoney(cents, this.rules.moneyFormat);
  }

  static formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!format) return `$${grouped}.${decimal}`;
    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${grouped}.${decimal}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, grouped)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /* -------------------------------------------------------------- rendering */

  /**
   * Re-rendering a group replaces its innerHTML, which throws focus back to the
   * body. A keyboard user activating an option would lose their place entirely,
   * so remember what was focused and put focus back on the equivalent node.
   */
  withFocusPreserved(render) {
    const active = document.activeElement;
    const inside = active && this.contains(active) ? active : null;
    const key = inside
      ? {
          choose: inside.dataset.bppChoose,
          value: inside.dataset.bppValue,
          topping: inside.dataset.bppTopping,
        }
      : null;

    render();

    if (!key) return;
    let restored = null;
    if (key.topping) {
      restored = this.querySelector(`[data-bpp-topping="${CSS.escape(key.topping)}"]`);
    } else if (key.choose && key.value) {
      restored = this.querySelector(
        `[data-bpp-choose="${CSS.escape(key.choose)}"][data-bpp-value="${CSS.escape(key.value)}"]`
      );
    }
    if (restored && restored !== document.activeElement) {
      restored.focus({ preventScroll: true });
    }
  }

  renderAll() {
    this.withFocusPreserved(() => this.renderAllInner());
    /* One call site, so no selection path can forget to update the address bar
       or the preview. The stage holds nothing focusable, so it is drawn outside
       withFocusPreserved — there is no focus for it to lose. */
    this.renderPreview();
    this.syncUrl();
  }

  renderAllInner() {
    this.renderOptions('size', 'sizes');
    this.renderOptions('crust', 'crusts');
    this.renderOptions('sauce', 'sauces');
    this.renderOptions('cheese', 'cheeses');
    this.renderOptions('style', 'styles');
    this.renderHalves();
    this.renderToppings();
    this.renderSummary();
    this.renderReview();
    this.renderTotals();
    this.renderSteps();
  }

  renderOptions(group, collection) {
    const host = this.querySelector(`[data-bpp-options="${group}"]`);
    if (!host) return;

    const items = this.config[collection] || [];
    const singular = group;

    host.innerHTML = items
      .map((item) => {
        const isOn = this.state[singular] === item.id;
        const soldOut = item.available === false;

        // A size's price is the base price, not an addition to it. Everything
        // else is an upcharge and reads as "+ $x".
        const isBasePrice = collection === 'sizes';
        const priceLine = isBasePrice && item.price
          ? `<span class="bpp-bld__opt-price">${this.money(item.price)}</span>`
          : '';
        const extra = !isBasePrice && item.price
          ? `<span class="bpp-bld__opt-extra">+ ${this.money(item.price)}</span>`
          : '';
        const media = item.image
          ? `<img class="bpp-bld__opt-img" src="${item.image}" alt="" width="56" height="56" loading="lazy">`
          : '';

        return `
          <button
            type="button"
            class="bpp-bld__opt${isOn ? ' is-selected' : ''}${soldOut ? ' is-unavailable' : ''}"
            data-bpp-choose="${singular}"
            data-bpp-value="${BppPizzaBuilder.escape(item.id)}"
            aria-pressed="${isOn}"
            ${soldOut ? 'disabled' : ''}
          >
            ${media}
            <span class="bpp-bld__opt-body">
              <span class="bpp-bld__opt-label">${BppPizzaBuilder.escape(item.label || '')}${item.spicy ? ' <span aria-label="spicy">🌶</span>' : ''}</span>
              ${item.dimension ? `<span class="bpp-bld__opt-dim">${BppPizzaBuilder.escape(item.dimension)}</span>` : ''}
              ${item.sublabel ? `<span class="bpp-bld__opt-sub">${BppPizzaBuilder.escape(item.sublabel)}</span>` : ''}
              ${item.description ? `<span class="bpp-bld__opt-sub">${BppPizzaBuilder.escape(item.description)}</span>` : ''}
              ${item.serves ? `<span class="bpp-bld__opt-sub">${BppPizzaBuilder.escape(this.s('serves', { serves: item.serves }))}</span>` : ''}
              ${priceLine}
              ${extra}
            </span>
            <span class="bpp-bld__opt-check" aria-hidden="true"></span>
          </button>`;
      })
      .join('');
  }

  renderHalves() {
    const on = this.state.halfAndHalf;

    if (this.els.halfToggle) {
      this.els.halfToggle.setAttribute('aria-checked', String(on));
      this.els.halfToggle.classList.toggle('is-on', on);
    }
    if (this.els.halves) {
      this.els.halves.hidden = !on;
      this.els.halves.querySelectorAll('[data-bpp-half]').forEach((btn) => {
        const active = btn.dataset.bppHalf === this.activeHalf;
        btn.setAttribute('aria-pressed', String(active));
        btn.classList.toggle('is-active', active);
      });
    }

    if (this.els.toppingHint) {
      const max = Number(this.rules.maxToppings) || 0;
      const parts = [];
      if (on) {
        parts.push(this.s(this.activeHalf === 'left' ? 'addingToLeft' : 'addingToRight'));
      }
      if (max > 0) {
        parts.push(this.s('chosenCount', { count: this.toppingCount(), max }));
      }
      if (this.s('doubleHint')) parts.push(this.s('doubleHint'));
      this.els.toppingHint.textContent = parts.join(' ');
      this.els.toppingHint.hidden = parts.length === 0;
    }
  }

  /** The price under a topping's name: one portion, or the double's total. */
  toppingPriceLine(t, portions) {
    const each = this.portionCents(t);
    if (!each) return '';
    const esc = BppPizzaBuilder.escape;
    if (portions >= BppPizzaBuilder.MAX_PORTIONS) {
      return `<span class="bpp-bld__topping-price is-double">${esc(this.s('doubleOn', { price: this.money(each * portions) }) || '')}</span>`;
    }
    return `<span class="bpp-bld__topping-price">+ ${esc(this.money(each))}</span>`;
  }

  /** The control under a chosen topping that switches it between one and two portions.
   *  Two short lines (action, then price or "undo") so it fits a narrow tile without
   *  spilling past it. */
  doubleButton(t, portions) {
    if (portions < 1) return '';
    const esc = BppPizzaBuilder.escape;
    const on = portions >= BppPizzaBuilder.MAX_PORTIONS;
    const main = on ? this.s('doubleUndo') || '' : this.s('doubleCta') || '';
    const sub = on ? this.s('doubleUndoSub') || '' : `+ ${this.money(this.portionCents(t))}`;
    return `<button type="button" class="bpp-bld__double${on ? ' is-on' : ''}"
      data-bpp-double="${esc(t.id)}" aria-pressed="${on}"
      aria-label="${esc(on ? this.s('doubleUndoAria', { label: t.label || '' }) : this.s('doubleCtaAria', { label: t.label || '' }))}"><span class="bpp-bld__double-main">${esc(main)}</span>${sub ? `<span class="bpp-bld__double-sub">${esc(sub)}</span>` : ''}</button>`;
  }

  /** "$2.50 each" when every topping in a category costs the same. */
  categoryPrice(inCat) {
    const prices = [...new Set(inCat.map((t) => this.portionCents(t)))];
    if (prices.length !== 1 || !prices[0]) return '';
    return this.s('eachPrice', { price: this.money(prices[0]) }) || '';
  }

  renderToppings() {
    if (!this.els.toppingGroups) return;

    const key = this.state.halfAndHalf ? this.activeHalf : 'whole';
    const chosen = this.state.toppings[key];
    const categories = this.config.categories || [];
    const toppings = this.config.toppings || [];

    this.els.toppingGroups.innerHTML = categories
      .map((cat) => {
        const inCat = toppings.filter((t) => (t.category || 'other') === cat.id);
        if (!inCat.length) return '';

        const buttons = inCat
          .map((t) => {
            const portions = BppPizzaBuilder.portionsOf(chosen, t.id);
            const isOn = portions > 0;
            const isDouble = portions > 1;
            // Show when a topping sits on the other half too.
            const other = this.state.halfAndHalf
              ? this.state.toppings[key === 'left' ? 'right' : 'left'].includes(t.id)
              : false;

            return `
              <div class="bpp-bld__topping-cell">
              <button
                type="button"
                class="bpp-bld__topping${isOn ? ' is-selected' : ''}${isDouble ? ' is-double' : ''}${other ? ' is-other-half' : ''}"
                data-bpp-topping="${BppPizzaBuilder.escape(t.id)}"
                aria-pressed="${isOn}"
                ${isDouble ? `aria-label="${BppPizzaBuilder.escape(this.s('doubleName', { label: t.label || '' }))}"` : ''}
              >
                ${isDouble ? `<span class="bpp-bld__topping-double" aria-hidden="true">${BppPizzaBuilder.escape(this.s('doubleBadge') || '×2')}</span>` : ''}
                ${t.image ? `<img class="bpp-bld__topping-img" src="${t.image}" alt="" width="64" height="64" loading="lazy">` : '<span class="bpp-bld__topping-img bpp-bld__topping-img--blank"></span>'}
                <span class="bpp-bld__topping-label">${BppPizzaBuilder.escape(t.label || '')}${t.spicy ? ' <span aria-label="spicy">🌶</span>' : ''}</span>
                ${this.toppingPriceLine(t, portions)}
                <span class="bpp-bld__opt-check" aria-hidden="true"></span>
              </button>
              ${this.doubleButton(t, portions)}
              </div>`;
          })
          .join('');

        return `
          <fieldset class="bpp-bld__group">
            <legend class="bpp-eyebrow">${BppPizzaBuilder.escape(cat.label)}${
              this.categoryPrice(inCat)
                ? ` <span class="bpp-bld__cat-price">· ${BppPizzaBuilder.escape(this.categoryPrice(inCat))}</span>`
                : ''
            }</legend>
            <div class="bpp-bld__toppings">${buttons}</div>
          </fieldset>`;
      })
      .join('');
  }

  /* -------------------------------------------------------- live preview */

  /**
   * Draw the current state onto the stage: the sauce underneath, then one layer
   * per chosen topping, each clipped to its half when half & half is on.
   *
   * Driven by this.state rather than by reading the option tiles, so the stage
   * cannot disagree with what will be added to the cart.
   */
  renderPreview() {
    /* No base image set means no stage in the markup, and nothing to draw. */
    if (!this.els.layers) return;

    const frag = document.createDocumentFragment();

    /* Sauce first so the toppings sit on top of it, and never halved: a half
       and half is two sets of toppings on one pizza, not two sauces. */
    const sauce = this.find('sauces', this.state.sauce);
    const sauceArt = BppPizzaBuilder.layerSrc(sauce);
    if (sauceArt && !BppPizzaBuilder.isBundledSauceArt(sauceArt)) {
      frag.appendChild(BppPizzaBuilder.layerNode(sauceArt, null));
    }

    /* Half & half stores the two halves separately; a whole pizza uses .whole
       and its layers carry no data-half, so the CSS leaves them uncropped. */
    const sides = this.state.halfAndHalf
      ? [['left', this.state.toppings.left], ['right', this.state.toppings.right]]
      : [[null, this.state.toppings.whole]];

    const missing = [];

    sides.forEach(([side, ids]) => {
      const drawn = {};
      ids.forEach((id) => {
        drawn[id] = (drawn[id] || 0) + 1;
        const topping = this.findTopping(id);
        if (!topping) return;
        const art = BppPizzaBuilder.layerSrc(topping);
        if (!art) {
          /* Recorded once per topping even when it is on both halves, so the
             note does not report the same missing art twice. An unlabelled
             block is still skipped, it just has no name to announce. */
          const name = topping.label || '';
          if (name && !missing.includes(name)) missing.push(name);
          return;
        }
        frag.appendChild(BppPizzaBuilder.layerNode(art, side, drawn[id]));
      });
    });

    this.els.layers.replaceChildren(frag);

    if (this.els.halfLine) this.els.halfLine.hidden = !this.state.halfAndHalf;

    this.renderPreviewNote(missing);
  }

  /**
   * The stage itself is aria-hidden, so this note is all a screen reader gets
   * from it — and it is a role="status", so anything written here is spoken.
   * It therefore carries only what the controls do not already say:
   *
   *   - that half & half is on, which no other live region announces;
   *   - that a chosen topping has no art, which is the only reason the stage
   *     can disagree with the tiles.
   *
   * It deliberately does NOT repeat which half is being edited: renderHalves()
   * already writes that into the topping hint, and the half switcher does not
   * run through renderAll(), so a copy here would be stale exactly when it
   * mattered. And it does not nag "pick your toppings" — that is instruction,
   * not status, and it would fire every time a customer removed their last one.
   *
   * Written only when the text actually changes, so re-rendering on every click
   * does not re-announce a message the customer has already heard.
   */
  renderPreviewNote(missing) {
    const note = this.els.previewNote;
    if (!note) return;

    const parts = [];

    if (this.state.halfAndHalf) {
      parts.push(this.s('halfAndHalf') || 'Half & half');
    }

    if (missing.length === 1) {
      parts.push(
        this.s('previewMissingArt', { label: missing[0] }) ||
          `${missing[0]} has no preview art yet.`
      );
    } else if (missing.length > 1) {
      parts.push(
        this.s('previewMissingArtPlural', { count: missing.length }) ||
          `${missing.length} toppings have no preview art yet.`
      );
    }

    const text = parts.join(' — ');
    if (note.textContent !== text) note.textContent = text;
  }

  /**
   * One preview layer. Built as a node with its URL assigned as a property
   * rather than concatenated into markup: item.image is a merchant-controlled
   * value (an uploaded file, or a theme asset URL), and it is the one string
   * here that would otherwise land inside an attribute.
   */
  static layerNode(src, side, copy) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    if (side) img.dataset.half = side;
    /* The second portion of a doubled topping is the same art flipped top to
       bottom, so the pizza visibly carries more of it. A vertical flip keeps a
       half-clipped layer on its own half; a rotation would swap sides. */
    if (copy > 1) img.dataset.copy = String(copy);
    return img;
  }

  /** What one portion of this topping adds to the price, in cents. */
  portionCents(topping) {
    if (topping?.price) return topping.price;
    return Number(this.rules.pricePerExtra) || 0;
  }

  /** How many of the same topping one pizza (or one half) can carry. */
  static MAX_PORTIONS = 2;

  static portionsOf(list, id) {
    return (list || []).reduce((n, x) => n + (x === id ? 1 : 0), 0);
  }

  /** Union of two topping lists, keeping the larger portion count of each. */
  static mergePortions(a, b) {
    const out = [];
    [...(a || []), ...(b || [])].forEach((id) => {
      if (out.includes(id)) return;
      const n = Math.min(
        BppPizzaBuilder.MAX_PORTIONS,
        Math.max(BppPizzaBuilder.portionsOf(a, id), BppPizzaBuilder.portionsOf(b, id))
      );
      for (let i = 0; i < n; i += 1) out.push(id);
    });
    return out;
  }

  /** Topping names for display: "Bacon (double), Olives". */
  toppingNames(list) {
    const out = [];
    (list || []).forEach((id) => {
      const label = this.findTopping(id)?.label;
      if (!label || out.some((o) => o.id === id)) return;
      const n = BppPizzaBuilder.portionsOf(list, id);
      out.push({ id, name: n > 1 ? (this.s('doubleName', { label }) || `${label} x2`) : label });
    });
    return out.map((o) => o.name);
  }

  /**
   * A usable layer URL, or null when the option has no art.
   *
   * snippets/bpp-builder-data.liquid assigns `art_url` from
   * `block.settings.image | image_url` and only falls back to the bundled
   * bpp-sauce-*.svg / bpp-topping-*.svg when the block's `graphic` is set to
   * something other than blank or "none". So a block with no upload and no
   * graphic serialises as an empty string (image_url of nil) — falsy, but a
   * string all the same, and `<img src="">` re-requests the page and paints a
   * broken image. Guarded on the value, not on its truthiness alone, so a
   * missing layer degrades to simply not being drawn.
   */
  static layerSrc(item) {
    /* layer_image is the same art at stage width; image is the tile-width copy.
       Both are absent on an option with no art, and the bundled SVGs serialise
       identically to both. Falling back keeps a metaobject or a config written
       before layer_image existed drawing at tile resolution rather than not at
       all. */
    const src = item && (item.layer_image || item.image);
    return typeof src === 'string' && src.trim() !== '' ? src : null;
  }

  /** Two columns: the base (size, crust, sauce, cheese, style) | the toppings. */
  renderSummary() {
    if (!this.els.summary) return;
    const esc = BppPizzaBuilder.escape;
    const { size, crust, sauce, cheese, style } = this.selected();

    const line = (label) =>
      `<li class="bpp-bld__summary-row"><span class="bpp-bld__summary-dot" aria-hidden="true"></span>${esc(label)}</li>`;
    const empty = `<li class="bpp-bld__summary-row bpp-muted">${esc(this.s('nothingYet'))}</li>`;
    const column = (heading, items) =>
      `<li class="bpp-bld__summary-col">
        <span class="bpp-bld__summary-head">${esc(heading)}</span>
        <ul class="bpp-bld__summary-sublist" role="list">${items.join('') || empty}</ul>
      </li>`;

    const base = [];
    if (size) base.push(line(`${size.label} ${size.dimension || ''}`.trim()));
    if (crust) base.push(line(crust.label));
    if (sauce) base.push(line(sauce.label));
    if (cheese) base.push(line(this.s('cheeseLine', { label: cheese.label })));
    if (style) base.push(line(this.s('styleLine', { label: style.label })));

    let toppings;
    if (this.state.halfAndHalf) {
      toppings = ['left', 'right'].map((half) => {
        const names = this.toppingNames(this.state.toppings[half]);
        return `<li class="bpp-bld__summary-half"><span class="bpp-bld__summary-halflabel">${esc(
          this.s(half === 'left' ? 'leftShort' : 'rightShort')
        )}</span>${
          names.length ? esc(names.join(', ')) : `<span class="bpp-muted">${esc(this.s('nothingYet'))}</span>`
        }</li>`;
      });
    } else {
      toppings = this.toppingNames(this.state.toppings.whole).map((n) => line(n));
    }

    this.els.summary.innerHTML =
      column(this.s('baseHeading') || '', base) +
      column(this.s(this.state.halfAndHalf ? 'halfAndHalf' : 'toppingsHeading'), toppings);
  }

  renderReview() {
    if (!this.els.review) return;
    const props = this.lineItemProperties();
    const rows = Object.entries(props)
      .filter(([k, v]) => !k.startsWith('_') && v)
      .map(
        ([k, v]) =>
          `<div class="bpp-bld__review-row"><dt>${BppPizzaBuilder.escape(k)}</dt><dd>${BppPizzaBuilder.escape(v)}</dd></div>`
      )
      .join('');

    const upcharge = this.rules.pricingMode === 'none' ? 0 : this.chargeableUpchargeCents();
    this.els.review.innerHTML = `
      <dl class="bpp-bld__review-list">${rows}</dl>
      <dl class="bpp-bld__review-totals">
        <div class="bpp-bld__review-row"><dt>${BppPizzaBuilder.escape(this.s('reviewBase'))}</dt><dd>${this.money(this.basePriceCents())}</dd></div>
        ${upcharge ? `<div class="bpp-bld__review-row"><dt>${BppPizzaBuilder.escape(this.s('reviewOptions'))}</dt><dd>${this.money(upcharge)}</dd></div>` : ''}
        <div class="bpp-bld__review-row bpp-bld__review-row--total"><dt>${BppPizzaBuilder.escape(this.s('reviewTotal'))}</dt><dd>${this.money(this.totalCents())}</dd></div>
      </dl>`;
  }

  renderTotals() {
    if (this.els.total) this.els.total.textContent = this.money(this.totalCents());

    if (this.els.serves) {
      const serves = this.selected().size?.serves;
      this.els.serves.textContent = serves ? this.s('serves', { serves }) : '';
    }

    if (this.els.calories) {
      const range = this.rules.showCalories ? this.calorieRange() : null;
      if (range) {
        this.els.calories.textContent = this.s('calories', { low: range.low, high: range.high });
        this.els.calories.hidden = false;
      } else {
        this.els.calories.hidden = true;
      }
    }
  }

  renderSteps() {
    this.els.panels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.bppPanel) !== this.step;
    });
    this.els.stepBtns.forEach((btn) => {
      const n = Number(btn.dataset.bppStepBtn);
      btn.setAttribute('aria-current', n === this.step ? 'true' : 'false');
      btn.classList.toggle('is-active', n === this.step);
      btn.classList.toggle('is-done', n < this.step);
    });
    if (this.els.prev) this.els.prev.hidden = this.step === 1;
    if (this.els.next) this.els.next.hidden = this.step === this.els.panels.length;
    this.revealActiveStepTab();
  }

  /**
   * The step tabs scroll sideways on a phone — the rail is narrower than the
   * full set. Nudge the rail so the current step is visible, without letting
   * scrollIntoView drag the whole page vertically as a side effect.
   */
  revealActiveStepTab() {
    const rail = this.querySelector('.bpp-bld__steps-list');
    const tab = this.els.stepBtns?.find?.(
      (b) => Number(b.dataset.bppStepBtn) === this.step
    ) || [...(this.els.stepBtns || [])].find(
      (b) => Number(b.dataset.bppStepBtn) === this.step
    );
    if (!rail || !tab || rail.scrollWidth <= rail.clientWidth) return;

    const r = rail.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    const pad = 16;
    let delta = 0;
    if (t.left < r.left + pad) delta = t.left - r.left - pad;
    else if (t.right > r.right - pad) delta = t.right - r.right + pad;
    if (!delta) return;

    // Assigning scrollLeft rather than scrollBy({behavior:'smooth'}): the CSS
    // scroll-behavior on the rail animates this where the browser supports it,
    // and degrades to an instant jump where it does not. The scripted-smooth
    // form silently does nothing in several engines.
    rail.scrollLeft += delta;
  }

  /* ----------------------------------------------------------------- cart */

  /**
   * Line item properties. `_builder_state` is underscore-prefixed so it stays
   * hidden from the customer while still reaching the order — the kitchen
   * ticket is rebuilt from it.
   */
  lineItemProperties() {
    const { size, crust, sauce, cheese, style } = this.selected();
    const names = (list) => this.toppingNames(list).join(', ');

    // Property names are translated, because the customer reads them in the
    // cart and at checkout. The kitchen does not depend on them — _builder_state
    // carries language-independent block ids for that.
    const props = {};

    // Size is omitted when the linked variant already carries it: the item is
    // added by variant id, so Shopify prints the size under the line's own
    // options and a Size property beside it repeats the same fact. When the
    // product has no Size option the variant cannot say which size was chosen,
    // so the property stays and is the only record of it.
    if (!(size && size.sizeShownByVariant)) {
      props[this.s('propSize')] = size
        ? `${size.label}${size.dimension ? ` ${size.dimension}` : ''}`
        : '';
    }

    Object.assign(props, {
      [this.s('propCrust')]: crust?.label || '',
      [this.s('propSauce')]: sauce?.label || '',
      [this.s('propCheese')]: cheese?.label || '',
      [this.s('propStyle')]: style?.label || '',
    });

    if (this.state.halfAndHalf) {
      props[this.s('propLeft')] = names(this.state.toppings.left) || this.s('none');
      props[this.s('propRight')] = names(this.state.toppings.right) || this.s('none');
    } else {
      props[this.s('propWhole')] = names(this.state.toppings.whole) || this.s('none');
    }

    props._builder_state = JSON.stringify(this.state);

    return props;
  }

  /**
   * The picture of this pizza for the cart: one compact, hidden line item
   * property, `_bpp_preview`, that snippets/bpp-cart-pizza-thumb.liquid redraws
   * as a thumbnail (base photo + sauce + topping layers, each half clipped).
   *
   * WHY a property and not an image: the cart is server-rendered Liquid and is
   * re-rendered by Shopify's section API after every change, so anything the
   * thumbnail needs has to ride on the line item. A data URL of a canvas would
   * be tens of KB on every order; the layer art already lives on the CDN, so
   * only its paths are sent (~300 bytes). The underscore keeps it off the
   * customer's cart and checkout view; it does reach the order, like
   * `_builder_state`, which stays the kitchen's record.
   *
   * Format, `;`-separated (Liquid has no JSON parser, `split` is all it has):
   *   1;PREFIX;BASE;SAUCE;WHOLE;LEFT;RIGHT
   * PREFIX is the directory every path shares ("/cdn/shop/files/"), BASE and
   * SAUCE are a name under it (SAUCE may be empty), WHOLE/LEFT/RIGHT are
   * comma-separated names in stacking order. Only what renderPreview() draws is
   * sent, so the cart can never disagree with the stage. Returns '' (and the
   * cart falls back to the product image) when there is no stage or a path
   * cannot be carried safely.
   */
  previewProperty() {
    const base = this.querySelector('.bpp-bld__base');
    if (!this.els.layers || !base) return '';

    const basePath = BppPizzaBuilder.previewPath(base.getAttribute('src'));
    if (!basePath) return '';

    const sauce = this.find('sauces', this.state.sauce);
    const sauceArt = BppPizzaBuilder.layerSrc(sauce);
    const sauceSrc = sauceArt && !BppPizzaBuilder.isBundledSauceArt(sauceArt) ? sauceArt : null;

    const pathsFor = (ids) =>
      (ids || [])
        .map((id) => BppPizzaBuilder.layerSrc(this.findTopping(id)))
        .filter(Boolean)
        .map((src) => BppPizzaBuilder.previewPath(src))
        .filter(Boolean);

    const whole = this.state.halfAndHalf ? [] : pathsFor(this.state.toppings.whole);
    const left = this.state.halfAndHalf ? pathsFor(this.state.toppings.left) : [];
    const right = this.state.halfAndHalf ? pathsFor(this.state.toppings.right) : [];
    const saucePath = sauceSrc ? BppPizzaBuilder.previewPath(sauceSrc) : '';

    // Shortest shared directory, so "/cdn/shop/files/" is sent once, not per layer.
    const all = [basePath, saucePath, ...whole, ...left, ...right].filter(Boolean);
    const dirs = all.map((p) => p.slice(0, p.lastIndexOf('/') + 1).split('/'));
    let shared = dirs[0];
    dirs.forEach((d) => {
      let i = 0;
      while (i < shared.length && i < d.length && shared[i] === d[i]) i += 1;
      shared = shared.slice(0, i);
    });
    let prefix = shared.join('/');
    if (!prefix.endsWith('/')) prefix += '/';
    const rel = (p) => p.slice(prefix.length);

    return [
      '1',
      prefix,
      rel(basePath),
      saucePath ? rel(saucePath) : '',
      whole.map(rel).join(','),
      left.map(rel).join(','),
      right.map(rel).join(','),
    ].join(';');
  }

  /**
   * A CDN URL reduced to what the cart needs: no scheme, no query (the cart
   * asks for its own width), and no host when it is the store's own (modern
   * /cdn/shop/... URLs). A foreign host, such as cdn.shopify.com on older URL
   * styles, is kept as "//host/path". '' when the URL cannot be carried in a
   * `;` and `,` separated property.
   */
  static previewPath(url) {
    try {
      const u = new URL(url, window.location.href);
      const path = (u.host === window.location.host ? '' : `//${u.host}`) + u.pathname;
      return /[;,|\s"'<>\\]/.test(path) || path.length > 200 ? '' : path;
    } catch (error) {
      return '';
    }
  }

  /**
   * Ties a pizza to its "Pizza extras" line: the pizza carries _bpp_build, the
   * extras line _bpp_extras with the same value and _bpp_per, its quantity for
   * one pizza. The cart folds the extras into the pizza's card and keeps its
   * quantity in step with the pizza's (assets/bpp-builder-cart.js). Unique per
   * add, so two builds never merge into one line.
   */
  static buildId() {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  }

  async addToCart() {
    const size = this.selected().size;

    if (!size) {
      this.showError(this.s('chooseSizeFirst'));
      return;
    }
    if (!size.variantId) {
      this.showError(this.s('notLinked'));
      return;
    }

    this.setBusy(true);
    this.clearError();

    const build = BppPizzaBuilder.buildId();
    const pizzaProps = this.lineItemProperties();
    if (this.rules.pricingMode === 'extras_product') pizzaProps._bpp_build = build;
    // The cart's picture of the pizza. Set in every pricing mode, and absent
    // (so the cart falls back to the product image) when there is no stage.
    const preview = this.previewProperty();
    if (preview) pizzaProps._bpp_preview = preview;
    const items = [{ id: Number(size.variantId), quantity: 1, properties: pizzaProps }];

    // The pricing boundary. Everything above this line is mode-agnostic.
    const upcharge = this.upchargeCents();
    if (this.rules.pricingMode === 'extras_product' && upcharge > 0) {
      const plan = this.extrasPlan(upcharge);
      if (!plan.length) {
        // The quoted total includes extras there is no variant to charge with.
        // Adding the pizza alone would undercharge every order; refuse instead.
        this.setBusy(false);
        this.showError(this.s('addError'));
        console.warn('Pizza extras pricing is on but no "Pizza extras" product with variants is linked.');
        return;
      }
      const forLabel = `${this.productTitle} · ${size.label}`;
      plan.forEach((line) => {
        items.push({
          id: Number(line.id),
          quantity: line.qty,
          properties: {
            [this.s('propFor')]: forLabel,
            _bpp_extras: build,
            _bpp_per: String(line.qty),
          },
        });
      });
    }
    if (this.rules.pricingMode === 'upcharge_product' && upcharge > 0) {
      const unit = Number(this.rules.upchargeUnitCents) || 1;
      const variant = Number(this.rules.upchargeVariantId);
      if (variant) {
        items.push({
          id: variant,
          quantity: Math.round(upcharge / unit),
          properties: { _for: this.productTitle, _upcharge_for: 'custom-pizza' },
        });
      } else if (this.rules.pricingMode === 'upcharge_product' && upcharge > 0) {
        // The quoted total included this upcharge. Sending the size variant
        // alone would charge the customer less than the price they were shown,
        // silently, on every order. Refuse instead.
        this.setBusy(false);
        this.showError(this.s('addError'));
        console.warn('Upcharge pricing is on but no upcharge variant ID is set.');
        return;
      } else {
        console.warn('Upcharge pricing is on but no upcharge variant ID is set.');
      }
    }

    const drawer = document.querySelector('cart-drawer');
    const hasDrawer = drawer && typeof drawer.renderContents === 'function';
    const body = { items };
    if (hasDrawer) {
      // Ask for the drawer and the header bubble in the same request, as
      // Taste's product form does, so both show the new pizza at once.
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.s('addError'));
      }

      if (hasDrawer) {
        // Refresh BEFORE announcing. HulkApps rewrites cart prices client-side
        // when it hears cartUpdate; announcing first meant it rewrote markup
        // this method then replaced with raw server HTML, so a pizza with paid
        // toppings showed the unadjusted total. The theme's own cart-drawer
        // does the same thing in this order.
        //
        // An empty cart renders <cart-drawer class="is-empty">, which hides
        // the drawer's header and items. Swapping the contents does not touch
        // that class, so clear it the way Taste's product form does.
        drawer.classList.remove('is-empty');
        drawer.setActiveElement?.(this.els.addBtn);
        if (result.sections && result.sections['cart-drawer']) {
          drawer.renderContents(result);
        } else {
          await this.refreshCartDrawer(drawer);
          drawer.open();
        }
      } else {
        window.location.href = this.dataset.cartUrl || '/cart';
      }

      // Now that the markup on screen is final, let the rest of the theme --
      // and HulkApps -- act on it.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bpp-pizza-builder' });
      }
    } catch (error) {
      this.showError(error.message || this.s('addError'));
    } finally {
      this.setBusy(false);
    }
  }

  /** Pull fresh drawer markup so it shows the pizza that was just added. */
  async refreshCartDrawer(drawer) {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      if (!response.ok) return;
      const markup = await response.text();
      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('cart-drawer');
      if (fresh) {
        drawer.innerHTML = fresh.innerHTML;
        // WHY: <cart-drawer> keeps its own class across the swap, so a page loaded with an empty cart left is-empty on a full drawer (header hidden, lines collapsed).
        drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
        // The overlay's click-to-close listener was bound to the node we just
        // destroyed. cart-drawer.js re-binds it after its own swaps; do the same.
        drawer
          .querySelector('#CartDrawer-Overlay')
          ?.addEventListener('click', drawer.close.bind(drawer));
      }
    } catch (error) {
      console.error('Could not refresh the cart drawer', error);
    }
  }

  setBusy(busy) {
    // The payload is built before the request and read by the cart after it.
    // Anything the customer changes in between would be shown on screen but not
    // sent -- tap Add, then tap a bigger size, and the drawer shows the small
    // pizza while the builder reads large. Freeze the choices, not just the
    // button.
    this.busy = busy;
    if (!this.els.addBtn) return;
    this.els.addBtn.toggleAttribute('disabled', busy);
    this.els.addBtn.setAttribute('aria-busy', String(busy));
  }

  showError(message) {
    if (!this.els.error) return;
    // Unhide first: writing into a still-hidden role="alert" mutates a region
    // that is not being rendered, and most screen readers announce nothing.
    this.els.error.hidden = false;
    this.els.error.textContent = message;
  }

  clearError() {
    if (!this.els.error) return;
    this.els.error.textContent = '';
    this.els.error.hidden = true;
  }

  /**
   * The sauce art the theme ships (assets/bpp-sauce-*.png|svg) is tile art: a
   * bowl photograph, or a flat disc. Drawn over the preview base as a layer it
   * hid the pizza under a bowl of sauce. The base photograph already shows the
   * sauce, so only a sauce image the merchant uploaded is drawn as a layer.
   */
  static isBundledSauceArt(src) {
    return /\/bpp-sauce-[^/]*\.(png|svg)(\?|$)/.test(String(src));
  }

  static escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('bpp-pizza-builder')) {
  customElements.define('bpp-pizza-builder', BppPizzaBuilder);
}
